import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import Modal from '../../../../../../../components/Modal';
import TextInput from '../../../../../../../components/TextInput';
import { updateExtraGroup } from '../../../../../../../graphql/mutations';
import { Sheet } from '../../AllergenModal/styled';
import { Field } from '../../ProductEditModal/styled';
import Button from '../../../../../../../components/Button';
import ToggleSwitch from '../../../../../../../components/ToggleSwitch';
import { HeaderSmall } from '../../../../../../../components/Text';
import { isNullOrUndefined, changeOrderHelper } from '@/utils/data';
import Draggable from '../../../../../../../components/Draggable';
import Checkbox from '../../../../../../../components/Checkbox';
import {
  CheckboxLabel,
  CheckboxLabelContainer,
  ExtraName,
  InnerDraggableContainer,
} from './styled';
import { productCategories as productCategoriesMutation } from '../../../../../../../graphql/queries';
import i18n from '@/utils/i18n';
function ExtraGroupEditModal({ productId, extraGroup, storeId, onClose }) {
  const [values, setValues] = useState(extraGroup);
  const [updateExtraGroupMutation, { loading: updating }] =
    useMutation(updateExtraGroup);

  const touchedValues = useMemo(
    () =>
      Object.keys(values).reduce((touched, key) => {
        if (JSON.stringify(extraGroup[key]) !== JSON.stringify(values[key])) {
          return { ...touched, [key]: values[key] };
        }
        return touched;
      }, {}),
    [extraGroup, values],
  );

  // TODO: we should probably reuse this more (after merge)
  function handleChange({ target }) {
    const { value, name } = target;
    const type = target.getAttribute('type');
    const stateName = target.getAttribute('data-state-name') || name;
    let formattedValue = null;
    if (value === '') {
      formattedValue = undefined;
    } else {
      formattedValue = type === 'number' ? +value : value;
    }

    if (type === 'checkbox') formattedValue = value === 'true';
    setValues({ ...values, [stateName]: formattedValue });
  }

  function handleSave() {
    if (Object.keys(touchedValues).length) {
      let input = touchedValues;
      if (input.items) {
        input = { ...input, items: input.items.map((ex) => ex.extra_id) };
      }

      updateExtraGroupMutation({
        variables: {
          product_id: productId,
          group_id: extraGroup.extra_group_id,
          item: { name: extraGroup.name, ...input },
        },
        // needed for updating variants
        refetchQueries: [
          {
            query: productCategoriesMutation,
            variables: {
              id: storeId,
              filter: 'ALL',
            },
          },
        ],
        update: () => {
          onClose();
        },
      });
    }
  }

  function sortExtra(extraId, targetExtraId) {
    setValues({
      ...values,
      items: changeOrderHelper(
        values.items,
        'extra_id',
        extraId,
        targetExtraId,
      ),
    });
  }

  function onDragStartExtra(event) {
    event.dataTransfer.setData('text/plain', event.target.id);
  }

  function onDragOverExtra(event) {
    event.preventDefault();
  }

  function onDropExtra(event) {
    const extraIdToMove = event.dataTransfer.getData('text');
    let dragTarget = event.target;
    while (!dragTarget.getAttribute('draggable')) {
      dragTarget = dragTarget.parentElement;
    }
    sortExtra(extraIdToMove, dragTarget.id);
    event.dataTransfer.clearData();
  }

  function onIsDefaultCheckboxClick(extra_id) {
    let newDefaultExtras;
    const checked = values.default_extras.indexOf(extra_id) !== -1;
    if (checked) {
      newDefaultExtras = values.default_extras.filter((id) => id !== extra_id);
    } else {
      newDefaultExtras = [...values.default_extras, extra_id];
    }
    setValues({ ...values, default_extras: newDefaultExtras });
  }
  return (
    <Modal onClose={onClose}>
      <Sheet>
        <Field>
          <TextInput
            name="extra-group-name"
            data-state-name="name"
            value={values.name}
            onChange={handleChange}
            placeholder={i18n.t('Menu:extraGroupName')}
          />
        </Field>
        <Field>
          <TextInput
            name="minimum_extras"
            value={
              isNullOrUndefined(values.minimum_extras)
                ? ''
                : values.minimum_extras
            }
            onChange={handleChange}
            placeholder={i18n.t('Menu:minExtras')}
            type="number"
          />
        </Field>
        <Field>
          <TextInput
            name="maximum_extras"
            value={
              isNullOrUndefined(values.maximum_extras)
                ? ''
                : values.maximum_extras
            }
            onChange={handleChange}
            placeholder={i18n.t('Menu:maxExtras')}
            type="number"
          />
        </Field>
        <Field>
          <HeaderSmall>
            {i18n.t('Menu:drawerDefault', {
              state: values.is_open
                ? i18n.t('Menu:drawerDefaultStateOpen')
                : i18n.t('Menu:drawerDefaultStateClosed'),
            })}
          </HeaderSmall>
          <ToggleSwitch
            name="is_open"
            value={!!values.is_open}
            onChange={handleChange}
          />
        </Field>

        <Field>
          <HeaderSmall>{i18n.t('Menu:extras')}</HeaderSmall>
          {(values.items || []).map((extra) => (
            <Draggable
              key={extra.extra_id}
              id={extra.extra_id}
              draggable
              onDragStart={onDragStartExtra}
              onDragOver={onDragOverExtra}
              onDrop={onDropExtra}
            >
              <InnerDraggableContainer>
                <ExtraName>{extra.name}</ExtraName>
                <CheckboxLabelContainer>
                  <Checkbox
                    checked={
                      values.default_extras.indexOf(extra.extra_id) !== -1
                    }
                    onClick={() => onIsDefaultCheckboxClick(extra.extra_id)}
                  />
                  <CheckboxLabel
                    onClick={() => onIsDefaultCheckboxClick(extra.extra_id)}
                  >
                    {i18n.t('Menu:defaultSelected')}
                  </CheckboxLabel>
                </CheckboxLabelContainer>
              </InnerDraggableContainer>
            </Draggable>
          ))}
        </Field>

        <Button
          disabled={!Object.keys(touchedValues).length}
          loading={updating}
          onClick={handleSave}
        >
          {i18n.t('Menu:save')}
        </Button>
      </Sheet>
    </Modal>
  );
}

ExtraGroupEditModal.propTypes = {
  productId: PropTypes.string.isRequired,
  extraGroup: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  storeId: PropTypes.string.isRequired,
};

export default ExtraGroupEditModal;
